// Core packages
import { formatDate } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';

// Third party packages
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { TranslateService } from '@ngx-translate/core';
import * as FileSaver from "file-saver";
import { ToastrService } from "ngx-toastr";
import { combineLatest, merge, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, finalize, startWith, take, tap } from 'rxjs/operators';

// Core packages
import { MatDialog, MatOptionSelectionChange } from '@angular/material';
import { AuthService } from 'src/app/modules/auth/auth.service';
import { CompaniesService } from "src/app/modules/companies/companies.service";
import { DeadlinesService } from 'src/app/modules/deadlines/deadlines.service';
import { ConfirmDialogComponent } from 'src/app/shared/components/dialogs/confirm-dialog/confirm-dialog.component';
import { ListSetting } from 'src/app/shared/interfaces/list-setting.interface';
import { ICompany } from "src/app/shared/models/company/company.interface";
import { IUser } from 'src/app/shared/models/user/user.interface';
import { User } from "src/app/shared/models/user/user.model";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from 'src/app/shared/services/helper.service';
import { ListsService } from 'src/app/shared/services/lists.service';
import { ThemeService } from 'src/app/shared/services/theme.service';
import { DeadlinesDataSource } from 'src/app/shared/utils/data-sources/deadlines-data-source';


export const MY_FORMATS = {
  parse: {
    dateInput: 'MM/YYYY',
  },
  display: {
    dateInput: 'MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-suppliers-subscriptions-deadlines-list',
  templateUrl: './suppliers-subscriptions-deadlines-list.page.html',
  styleUrls: ['./suppliers-subscriptions-deadlines-list.page.scss'],
  providers: [
    // `MomentDateAdapter` can be automatically provided by importing `MomentDateModule` in your
    // application's root module. We provide it at the component level here, due to limitations of
    // our example generation script.
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
    },

    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
})
export class SuppliersSubscriptionsDeadlinesListComponent implements OnInit, AfterViewInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];

  // BEGIN - Table deadlines
  displayedColumns: string[] = ['index', 'master', 'endDateSuppliers', 'propertiesNumber', 'suppliersNumber', 'price', 'actions', 'renew'];
  dataSource: DeadlinesDataSource;
  @ViewChild('sort1', { static: true }) sort: MatSort;
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  // @ViewChild('input', { static: true }) input: ElementRef;
  masterFilter: FormControl = new FormControl('');
  endDateSuppliersFilter: FormControl = new FormControl('');
  propertiesNumberFilter: FormControl = new FormControl('');
  suppliersNumberFilter: FormControl = new FormControl('');
  priceFilter: FormControl = new FormControl('');
  // END - Table deadlines

  activeRoute: string;
  masterIsLoading = false;
  availableMasters: ICompany[] = [];
  availableStatus = [];
  roleType: string;

  selectedSubscriptions: any[] = [];

  roleAdmin = false;
  adminIsLoading = false;
  availableAdmins: User[] = [];

  /**
   * Class constructor
   */
  constructor(
    public dialog: MatDialog,

    private deadlinesService: DeadlinesService,
    private helperService: HelperService,
    private authService: AuthService,
    private translateService: TranslateService,
    private themeService: ThemeService,
    private listsService: ListsService,
    private configService: ConfigService,
    private toastrService: ToastrService,
    private companiesService: CompaniesService,
  ) { }

  /**
   * Init component
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Set page title
    this.themeService.setPageTitle(this.translateService.instant('deadlines.suppliersSubscriptionsList.pageTitle'));

    // Get logged user data
    this.subscriptions.push(this.authService.loggedUser$.subscribe(res => this.loggedUser = res));
    this.subscriptions.push(this.authService.loggedUserPermissions$.subscribe(res => this.loggedUserPermissions = res));
    this.subscriptions.push(this.authService.loggedUserRequestedRoleType$.subscribe(res => {
      this.roleType = res;
      if (
        this.roleType === "master"
        || this.roleType === "collaborator"
      ) {
        this.displayedColumns.splice(this.displayedColumns.indexOf("propertiesNumber"), 1);
        this.displayedColumns.splice(this.displayedColumns.indexOf("suppliersNumber"), 1);
        this.displayedColumns.splice(this.displayedColumns.indexOf("price"), 1);
        this.displayedColumns.splice(this.displayedColumns.indexOf("actions"), 1);
        this.displayedColumns.splice(this.displayedColumns.indexOf("invoiceDate"), 1);
      }

      if (
        this.roleType === "admin"
        || this.roleType === "operator"
      ) {
        this.roleAdmin = true;
      }
    }));

    // Init data source
    this.dataSource = new DeadlinesDataSource(this.deadlinesService, this.helperService, this.translateService);

    // Subscribe to listsService and update this list settings
    this.subscribeToListsSettings('deadlines');

    this.getAvailableAdmins();
  }

  /**
   * After view init logic
   *
   * @since 1.0.0
   */
  ngAfterViewInit(): void {
    // Load first set of data
    this.loadPage();

    // Reset pagination after sorting
    this.subscriptions.push(this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0));

    // Once user change sorting OR change page
    // let's reload the page data
    this.subscriptions.push(
      merge(this.sort.sortChange, this.paginator.page)
        .pipe(
          tap(() => this.loadPage())
        )
        .subscribe()
    );

    // Handle server-side filtering through general search
    // this.subscriptions.push(
    //   fromEvent(this.input.nativeElement, 'keyup')
    //     .pipe(
    //       debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
    //       distinctUntilChanged(), // Eliminate duplicate values
    //       tap(() => {
    //         this.paginator.pageIndex = 0; // Reset page
    //         this.loadPage();
    //       })
    //     )
    //     .subscribe()
    // );

    // Handle server-side filtering through column-specific search
    // Why do we use "null as string"? --> @see https://github.com/ReactiveX/rxjs/issues/4772
    this.subscriptions.push(
      combineLatest([
        this.masterFilter.valueChanges.pipe(startWith(null as string)),
        this.endDateSuppliersFilter.valueChanges.pipe(startWith(null as string)),
        this.propertiesNumberFilter.valueChanges.pipe(startWith(null as string)),
        this.suppliersNumberFilter.valueChanges.pipe(startWith(null as string)),
        this.priceFilter.valueChanges.pipe(startWith(null as string)),
      ])
        .pipe(
          debounceTime(this.configService.debounceTime), // max 1 query every 350 milliseconts
          distinctUntilChanged(), // Eliminate duplicate values
        )
        .subscribe(res => {
          // Handle master filter
          if (res) {
            this.masterIsLoading = true;
            this.getAvailableMasters({
              general: this.masterFilter.value
            });
          }


          // Load page only if at least one value is not null
          if (!res.every(value => value === null)) {
            this.paginator.pageIndex = 0;
            this.loadPage();
          }
        }
        )
    );
  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all deadlines
    this.subscriptions.forEach(sub => sub.unsubscribe());

    // Push current list settings to the listsService
    this.listsService.setListSetting(
      'deadlines',
      this.paginator.pageIndex.valueOf(),
      this.paginator.pageSize.valueOf(),
      {
        general: '', // this.input.nativeElement.value,
        master: this.masterFilter.value,
        endDateSuppliers: this.endDateSuppliersFilter.value,
        propertiesNumber: this.propertiesNumberFilter.value,
        suppliersNumber: this.suppliersNumberFilter.value,
        price: this.priceFilter.value,
      }
    );
  }

  /**
   * Load requested data page
   *
   * @since 1.0.0
   */
  loadPage(): void {
    const start = this.paginator.pageIndex * this.paginator.pageSize;
    const length = this.paginator.pageSize;
    const search = {
      general: '', // this.input.nativeElement.value,
      master: this.masterFilter.value ? this.masterFilter.value._id : undefined,
      endDateSuppliers: this.endDateSuppliersFilter.value,
      propertiesNumber: this.propertiesNumberFilter.value,
      suppliersNumber: this.suppliersNumberFilter.value,
      price: this.priceFilter.value,
    };
    this.dataSource.loadSubscriptionsSuppliersItems(start, length, this.sort.active, this.sort.direction, search);
  }

  /**
   * Export deadlines in a file with given format
   *
   * @since 1.0.0
   */
  onExport(): void {
    const date = formatDate(new Date(), 'yyyyMMddhhmmss', 'en-EN');
    const fileName = `${this.translateService.instant('deadlines.suppliersSubscriptionsList.exportFileName')} ${date}`;
    this.subscriptions.push(
      this.deadlinesService.getSubscriptionsSuppliersExport().subscribe(
        res => {
          const fileBlob = res;
          const blob = new Blob([fileBlob], {
            type: 'application/vnd.ms-excel'
          });
          FileSaver.saveAs(blob, `${fileName}.xlsx`);
        },
        err => {
          this.helperService.handleError(err);
        }
      )
    );
  }

  /**
   * Reset table's filters
   *
   * @since 1.0.0
   */
  onResetFilters(): void {
    // Reset lists service data
    this.listsService.setListSetting('deadlines', 0, 10, {});

    // Reset tables' filters
    // this.input.nativeElement.value = '';
    this.masterFilter.setValue(null);
    this.endDateSuppliersFilter.setValue(null);
    this.propertiesNumberFilter.setValue(null);
    this.suppliersNumberFilter.setValue(null);
    this.priceFilter.setValue(null);

    // Reset tables' sorting versus and column
    this.sort.direction = "asc";
    this.sort.active = "master";
    this.sort._stateChanges.next();

    // Reset paginator
    this.paginator.pageIndex = 0;
    this.paginator.pageSize = 10;

    // Reload table's data
    this.loadPage();
  }

  /**
   * Reset given filter value and reload the page
   *
   * @since 1.0.0
   */
  onResetFilter(filter: FormControl): void {
    filter.reset();
    this.loadPage();
  }

  /**
   * Subscribe to lists settings service to allow this list
   * to remember current filters once users navigate to another page.
   * In this way once user will come back to this page he will
   * have all previous settings (such as page number, page size, search, ...)
   *
   * @since 1.0.0
   */
  subscribeToListsSettings(name: string): void {
    this.listsService.enable();
    this.subscriptions.push(
      this.listsService.listsSettings$.subscribe(
        (settings: ListSetting[]) => {
          if (settings.length) {
            settings.forEach((setting: ListSetting) => {
              if (setting.name === name) {
                if (setting.pageIndex) {
                  this.paginator.pageIndex = setting.pageIndex;
                }
                if (setting.pageSize) {
                  this.paginator.pageSize = setting.pageSize;
                }
                if (setting.search && setting.search.general) {
                  // this.input.nativeElement.value = setting.search.general;
                }
                if (setting.search && setting.search.master) {
                  this.masterFilter.setValue(setting.search.master);
                }
                if (setting.search && setting.search.endDateSuppliers) {
                  this.endDateSuppliersFilter.setValue(setting.search.endDateSuppliers);
                }
                if (setting.search && setting.search.propertiesNumber) {
                  this.propertiesNumberFilter.setValue(setting.search.propertiesNumber);
                }
                if (setting.search && setting.search.suppliersNumber) {
                  this.suppliersNumberFilter.setValue(setting.search.suppliersNumber);
                }
                if (setting.search && setting.search.price) {
                  this.priceFilter.setValue(setting.search.price);
                }
              }
            });
          }
        }
      )
    );
  }

  /**
   * Reset general search input and dispath a 'keyup' event
   * so that the table re-render his rows
   *
   * @since 1.0.0
   */
  resetInput(): void {
    // this.input.nativeElement.value = '';
    const event = new KeyboardEvent('keyup');
    // this.input.nativeElement.dispatchEvent(event);
  }


  /**
   * Get the list of available admins and owners
   * used to allow filtering on owner and admin columns
   *
   * @since 1.0.0
   */
  getAvailableMasters(search?: any): void {
    this.subscriptions.push(
      this.companiesService
        .getList(0, null, null, null, search)
        .pipe(
          finalize(() => {
            this.masterIsLoading = false;
          })
        )
        .subscribe(res => {
          if (res.status) {
            this.availableMasters = res.data.items;
          }
        })
    );
  }

  /**
   * Used to handle the display of selected result
   *
   * @since 1.0.0
   *
   * @param arg Data to be displayed
   * @returns result string to display or undefined
   */
  displayFn(arg?: ICompany): string | undefined {
    return arg ? `${arg.name}` : undefined;
  }

  /**
   * Handle click on a selected master in the mat-autocomplete list and fires the loadPage()
   *
   * @since 1.0.0
   */
  onMasterFilterSelected(event: MatOptionSelectionChange): void {
    const value = event.source.value;
    this.masterFilter.setValue(value, { emitEvent: false });
    this.loadPage();
  }

  /**
   * Handle list of masters subscription to renew
   *
   * @since 1.0.0
   */
  onSubsSelected(subscription: any, checked: boolean): void {
    if (subscription && checked) {
      this.selectedSubscriptions.push(subscription._id);
    } else if (subscription && !checked) {
      const index = this.selectedSubscriptions.indexOf(subscription._id);
      this.selectedSubscriptions.splice(index, 1);
    }
  }

  /**
   * Renew the deadlines for masters subscriptons
   *
   * @since 1.0.0
   */
  onRenewDeadlines(): void {
    if (!this.selectedSubscriptions.length) {
      this.toastrService.error(this.translateService.instant('common.errors.chooseElementFirst'));
      return;
    }
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('dialogs.renewSubscriptions.title'),
        message: this.translateService.instant('dialogs.renewSubscriptions.message'),
        btnOkText: this.translateService.instant('common.cta.confirm'),
        btnCancelText: this.translateService.instant('common.cta.close'),
      },
      width: '500px',
      disableClose: true
    });
    // Subscribe to dialog result (only for 1 emit thanks to "take()")
    dialogRef.beforeClosed()
      .pipe(
        take(1)
      )
      .subscribe(
        res2 => {
          if (res2) {
            const data = {
              companies: this.selectedSubscriptions,
            };
            this.masterIsLoading = true;
            this.subscriptions.push(
              this.deadlinesService.renewSubscriptionsSuppliersDeadline(data)
                .subscribe(
                  res => {
                    if (res.status) {
                      this.masterIsLoading = false;
                      this.toastrService.success(this.translateService.instant('deadlines.suppliersSubscriptionsList.renewSuccessfully'));

                      this.selectedSubscriptions = [];
                      this.loadPage();
                    }
                  },
                  err => {
                    this.helperService.handleError(err);
                  }
                )
            );
          }
        }
      );
  }

  /**
   * Used to handle the display of selected result
   *
   * @since 1.0.0
   *
   * @param arg Data to be displayed
   * @returns result string to display or undefined
   */
  displayAdminFn(arg?: ICompany): string | undefined {
    return arg ? `${arg.name}` : undefined;
  }

  /**
   * Get the list of available admins and owners
   * used to allow filtering on owner and admin columns
   *
   * @since 1.0.0
   */
  getAvailableAdmins(search?: any): void {
    this.subscriptions.push(
      this.companiesService
        .getList(0, 10, null, null, search)
        .pipe(
          finalize(() => {
            this.adminIsLoading = false;
          })
        )
        .subscribe(res => {
          if (res.status) {
              this.availableAdmins = res.data.items;
          }
        })
    );
  }

  /**
   * Export all properties paying suppliers subscription
   *
   * @since 1.0.0
   */
  onDownloadProperties(element: any): void {
    console.log("element",element)
    const date = formatDate(new Date(), 'yyyyMMddhhmmss', 'en-EN');
    const fileName = `${this.translateService.instant('deadlines.suppliersSubscriptionsList.exportFileName')} ${date}`;
    this.subscriptions.push(
      this.deadlinesService.getSubscriptionsPropertiesExport(element._id).subscribe(
        res => {
          const fileBlob = res;
          const blob = new Blob([fileBlob], {
            type: 'application/vnd.ms-excel'
          });
          FileSaver.saveAs(blob, `${fileName}.xlsx`);
        },
        err => {
          this.helperService.handleError(err);
        }
      )
    );
  }

}
